import { Http } from '@/lib/http';

export class LoanPoolService extends Http {
  constructor() {
    super();
    this.endpoint = 'loan-pro/app/loans/';
  }

  /**
   * The `searchLoan` function sends a POST request to the `search` endpoint with a payload and
   * optional parameters.
   * @param payload - The `payload` parameter is the data that will be used to filter the loans
   * @param params - The query params for pagination
   * @example
   * const payload = {
   *   loanProgresses: string[] - List of loan progresses to filter by
   *   userIds: string[] - List of user ids to filter by
   *   accreditedBranchIds: string[] - List of branch ids to filter by
   *   search: string - Used to filter loans by account number or persons on the account.
   *   isAssigned: boolean - If true it returns all loans that are assigned to a user other returns all unassigned loans. Remove this property to return all loans wether assigned or not.
   * }
   *
   * const params = {
   *  offset: number - The number of items to skip
   *  limit: number - The number of items to return
   * }
   *
   */
  searchLoan(payload, params) {
    return this.httpClient().post(`${this.endpoint}search`, payload, {
      params: params
    });
  }

  /**
   * The `findWorkloadLoans` function sends a POST request to the workload endpoint get all loans for a specific user workload
   * @param payload - The `payload` parameter is the data that will be used to filter the loans
   * @example
   * const payload = {
   *   loanProgresses: string[] - List of loan progresses to filter by
   *   userId: string - User ids to filter by
   * }
   * @returns {
   *    data: Array<Objects> - list of loans,
   *    total: number - total loans
   * }
   */
  findWorkloadLoans(payload) {
    return this.httpClient().post(`${this.endpoint}workload`, payload);
  }

  /**
   * The createLoan function sends a POST request to the specified endpoint with the given payload.
   * @param payload - The details of the loan to be created
   * @returns {Promise<AxiosResponse<Object>>} The created loan
   */
  createLoan(payload) {
    return this.httpClient().post(`${this.endpoint}`, payload);
  }

  /**
   * Updates the term and balances of loans on a specific account
   * @param Array<{
   *  coreId: string,
   *  interest: number,
   *  loanAmount: number,
   *  maturityDate: Date | string,
   *  dateDisbursed: Date | string,
   *  frequencyNoun: string,
   *  frequencyName: string,
   *  currentBalance: number,
   *  frequencyPayment: number,
   * }> payload - An array of loans to be updated
   * @returns {Promise<AxiosResponse<Object>>} The updated loans
   */
  integrationBatchUpdateLoan(payload) {
    return this.httpClient().put(`${this.endpoint}integration-batch`, payload);
  }

  /**
   * The updateLoan function sends a PATCH request to the specified endpoint with the given payload.
   * @param payload - The details of the loan to be updated (id must be present)
   * @returns {Promise<AxiosResponse<Object>>} The updated loan
   */
  updateLoan(payload) {
    return this.httpClient().patch(`${this.endpoint}${payload.id}`, payload);
  }

  /**
   * Find all loans consolidated or top up by the given loan id
   * @param { string } loanId - loan id
   * @returns {Promise<AxiosResponse<Object>>} Array of loans
   */
  findConsolidatedLoans(loanId) {
    return this.httpClient().get(
      `${this.endpoint}consolidated-loans/${loanId}`
    );
  }

  /**
   * The saveAffordabilityTest function sends a POST request to the specified endpoint with the given payload.
   * @param { string } loanId - The id of the loan processing
   * @param { string } personId - The id of the person to which the affordability test belongs
   * @param { object } payload - The affordability test details
   * @returns {Promise<AxiosResponse<Object>>} The updated loan
   */
  saveAffordabilityTest(loanId, personId, payload) {
    return this.httpClient().post(
      `${this.endpoint}${loanId}/affordability-test/${personId}`,
      payload
    );
  }

  /**
   * Find previous affordability test for a person
   * @param { string } personId - The id of the person of which to search
   * @returns {Promise<AxiosResponse<Object>>} The affordability test object
   */
  findPreviousAffordabilityTest(personId) {
    return this.httpClient().get(
      `${this.endpoint}affordability-test/${personId}`
    );
  }

  /**
   * The saveCreditAssessmentStatus save credit assessment status with adjusted terms
   * @param { string } loanId - The id of the loan processing
   * @param { object } payload - The credit assessment status and adjusted terms
   * @returns {Promise<AxiosResponse<Object>>} The updated loan
   */
  saveCreditAssessmentStatus(loanId, payload) {
    return this.httpClient().post(
      `${this.endpoint}${loanId}/credit-assessment-status`,
      payload
    );
  }

  /**
   * The saveCreditAssessment function sends a POST request to the specified endpoint with the given payload.
   * @param { string } loanId - The id of the loan processing
   * @param { object } payload - The credit assessment details
   * @returns {Promise<AxiosResponse<Object>>} The updated loan
   */
  saveCreditAssessment(loanId, payload) {
    return this.httpClient().post(
      `${this.endpoint}${loanId}/credit-assessment`,
      payload
    );
  }

  /**
   * Updates a checklist item on a loan application
   * @param { string } loanId - The id of the loan
   * @param { object } payload - The checklist item details
   * @returns {Promise<AxiosResponse<Object>>} The updated loan
   */
  updateChecklistItem(loanId, payload) {
    return this.httpClient().patch(
      `${this.endpoint}${loanId}/checklist-item`,
      payload
    );
  }

  /**
   * Get a loan by its id
   *
   * @param { String} id - The loan uuid
   */
  getLoanById(id) {
    return this.httpClient().get(`${this.endpoint}${id}`);
  }

  /**
   * This method sends a request to assign the loan to a user
   *
   * @param {object} payload - The payload containing the `loanId` and the `userId`
   *
   * @example
   *
   * const payload = {
   *  loanId: "654654564",
   *  userId: "6546-54564-asdasd5-asd5asd-asd544"
   * }
   *
   * const service = new LoanPoolService();
   * await service.assignLoanToUser(payload);
   *
   */
  assignLoanToUser(payload) {
    return this.httpClient().patch(
      `${this.endpoint}${payload.loanId}/assign/processing/${payload.userId}`
    );
  }

  /**
   * Handles the deasignment based on the loan progress
   *
   * @param { object } loan - the loan object
   */
  async deassignLoan(loan) {
    switch (loan.loanProgress?.toLowerCase()) {
      case 'loan review':
      case 'securities check':
        await this.deassignChecklistFromUser({
          loanId: loan.id
        });
        break;
      case 'waiver requested':
      case 'approval requested':
        await this.cancelApprovalRequest({
          loanId: loan.id
        });
        break;
      default:
        await this.deassignLoanFromUser({
          loanId: loan.id
        });
    }
  }

  /**
   * this method sends a request to deassign the loan from a user
   *
   * @param {object} payload - the payload containing the `loanid`
   *
   * @example
   *
   * const payload = {
   * loanid: "654654564",
   * }
   *
   * const service = new loanpoolservice();
   * await service.deassignloanfromuser(payload);
   */
  deassignLoanFromUser(payload) {
    return this.httpClient().delete(
      `${this.endpoint}${payload.loanId}/assign/processing`
    );
  }

  /**
   * Assigns a checklist to a reviewer
   * @param {object} payload - The payload containing the `loanId` and the `userId`
   *
   * @example
   *
   * const payload = {
   *  loanId: "654654564",
   *  userId: "6546-54564-asdasd5-asd5asd-asd544"
   * }
   *
   * const service = new LoanPoolService();
   * await service.assignChecklistToReviewer(payload);
   *
   */
  assignChecklistToReviewer(payload) {
    return this.httpClient().patch(
      `${this.endpoint}${payload.loanId}/assign/checklist/${payload.userId}`
    );
  }

  /**
   * Unassign a checklist from a user
   *
   * @param {object} payload - The payload containing the `loanId`
   *
   * @example
   *
   * const payload = {
   *  loanId: "654654564",
   * }
   *
   * const service = new LoanPoolService();
   * await service.deassignChecklistFromUser(payload);
   */
  deassignChecklistFromUser(payload) {
    return this.httpClient().delete(
      `${this.endpoint}${payload.loanId}/assign/checklist`
    );
  }

  assignLoanForApproval(payload) {
    return this.httpClient().patch(
      `${this.endpoint}${payload.loanId}/assign/approval/${payload.userId}`
    );
  }

  /**
   * Cancel an already sent approval request
   *
   * @param {object} payload - The payload containing the `loanId`
   *
   * @example
   *
   * const loan = {
   *   id: "654654564",
   * }
   *
   * const service = new LoanPoolService();
   * await service.cancelApprovalRequest(loan.id);
   */
  cancelApprovalRequest(loanId, type) {
    return this.httpClient().delete(
      `${this.endpoint}${loanId}/assign/approval/${type ?? 'loan-approval'}`
    );
  }

  approveLoan(loanId) {
    return this.httpClient().post(`${this.endpoint}${loanId}/approve`);
  }

  rejectLoanApproval(loanId) {
    return this.httpClient().delete(`${this.endpoint}${loanId}/approve`);
  }

  assignForWaiverApproval(payload) {
    return this.httpClient().patch(
      `${this.endpoint}${payload.loanId}/assign/waiver/${payload.userId}`
    );
  }

  approveLoanWaiver(loanId) {
    return this.httpClient().post(`${this.endpoint}${loanId}/waive-terms`);
  }

  rejectLoanWaiverRequest(loanId) {
    return this.httpClient().delete(`${this.endpoint}${loanId}/waiver`);
  }

  /**
   * This action sends a request to terminate a loan
   *
   * @param {Object} payload - The payload containing the `loanId` and the `terminationReason`
   *
   * @example
   *
   * const payload = {
   * loanId: "654654654",
   * type: "cancel", // deny
   * reason: "The reason for the termination"
   * comments: "The comments for the termination"
   * }
   *
   * const service = new LoanPoolService();
   * await service.terminateLoan(payload);
   */
  terminateLoan(payload) {
    return this.httpClient().patch(
      `${this.endpoint}${payload.loanId}/terminate`,
      payload
    );
  }

  /**
   * The savePartyToLoan function save a person as a guarantor, co-borrower or co-maker to a loan
   * @param loanId - The loan id
   * @param payload - The details of the party to be created
   * @returns {Promise<AxiosResponse<Object>>} The created party details
   */
  savePartyToLoan(loanId, payload) {
    return this.httpClient().patch(
      `${this.endpoint}${loanId}/party-to-loan`,
      payload
    );
  }

  /**
   * This action sends a request to delete a loan permanently
   *
   * @param {string} loanId - The id of the loan to be deleted
   *
   * @example
   *
   * const service = new LoanPoolService();
   * await service.deleteLoanPermanently("654654654");
   */
  deleteLoanPermanently(loanId) {
    return this.httpClient().delete(`${this.endpoint}${loanId}`);
  }

  /**
   * This action sends a request to delete a party to loan
   *
   * @param {string} loanId - The id of the loan the distribution is on
   * @param {string} personId - The id of the person the distribution belongs to
   *
   * @example
   *
   * const service = new LoanPoolService();
   * await service.deletePartyToLoan("654654654", "89290320");
   */
  deletePartyToLoan(loanId, personId) {
    return this.httpClient().delete(
      `${this.endpoint}${loanId}/party-to-loan/${personId}`
    );
  }

  createLoanDisbursementSchedule(loanId, payload) {
    return this.httpClient().put(
      `${this.endpoint}${loanId}/disbursement-schedule`,
      payload
    );
  }

  deleteLoanDisbursementSchedule(loanId) {
    return this.httpClient().delete(
      `${this.endpoint}${loanId}/disbursement-schedule`
    );
  }

  updateLoanProgress(loanId, progress) {
    return this.httpClient().patch(
      `${this.endpoint}${loanId}/update-progress`,
      progress
    );
  }

  disburseExternalLoanSchedule(loanId, scheduleId, schedulePayload) {
    return this.httpClient().patch(
      `${this.endpoint}${loanId}/disburse-loan/${scheduleId}`,
      schedulePayload
    );
  }

  exportToExcel(query, filters) {
    return this.httpClient().post(
      `${this.endpoint}reports/excel?${query}`,
      filters,
      { responseType: 'arraybuffer' }
    );
  }

  searchMissingLoanIds(payload, params) {
    return this.httpClient().post(
      `${this.endpoint}missing-loan-ids/search`,
      payload,
      {
        params: params
      }
    );
  }

  updateMissingLoanId(loanId, coreId) {
    return this.httpClient().patch(`${this.endpoint}${loanId}/core-id`, {
      coreId
    });
  }
}
